let ctx = null;
let master = null;
let compressor = null;
let humOsc = null;
let humOsc2 = null;
let humGain = null;
let humNoise = null;
let humNoiseGain = null;

function getContext() {
    if (!ctx) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return null;
        ctx = new AudioContextClass();
        master = ctx.createGain();
        compressor = ctx.createDynamicsCompressor();
        master.gain.value = 0.48;
        compressor.threshold.value = -18;
        compressor.knee.value = 18;
        compressor.ratio.value = 5;
        compressor.attack.value = 0.004;
        compressor.release.value = 0.18;
        master.connect(compressor);
        compressor.connect(ctx.destination);
    }
    if (ctx.state === "suspended") ctx.resume();
    return ctx;
}

function tone(freq, duration, type = "sine", volume = 0.15, endFreq = null, delay = 0) {
    const c = getContext();
    if (!c || !master) return;
    const osc = c.createOscillator();
    const gain = c.createGain();
    const start = c.currentTime + delay;
    osc.type = type;
    osc.frequency.setValueAtTime(Math.max(1, freq), start);
    if (endFreq) osc.frequency.exponentialRampToValueAtTime(Math.max(1, endFreq), start + duration);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.linearRampToValueAtTime(volume, start + Math.min(0.028, duration * 0.2));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(gain);
    gain.connect(master);
    osc.start(start);
    osc.stop(start + duration + 0.06);
}

function noise(duration = 0.3, volume = 0.05, filterFreq = 700, delay = 0, type = "bandpass") {
    const c = getContext();
    if (!c || !master) return;
    const length = Math.max(1, Math.floor(c.sampleRate * duration));
    const buffer = c.createBuffer(1, length, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
    const source = c.createBufferSource();
    const filter = c.createBiquadFilter();
    const gain = c.createGain();
    const start = c.currentTime + delay;
    source.buffer = buffer;
    filter.type = type;
    filter.frequency.value = filterFreq;
    filter.Q.value = type === "bandpass" ? 0.8 : 0.55;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.linearRampToValueAtTime(volume, start + Math.min(0.03, duration * 0.2));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(master);
    source.start(start);
    source.stop(start + duration + 0.04);
}

function relayCluster(count = 4, spacing = 0.12, startDelay = 0) {
    for (let i = 0; i < count; i += 1) {
        const delay = startDelay + i * spacing;
        tone(118 + i * 17, 0.055, "square", 0.055, 82, delay);
        noise(0.055, 0.018, 1600 + i * 120, delay);
    }
}

function heartbeat(volume = 0.18, delay = 0) {
    tone(72, 0.13, "sine", volume, 54, delay);
    tone(48, 0.18, "sine", volume * 0.72, 39, delay + 0.19);
}

function makeLoopNoise(c) {
    const seconds = 2;
    const buffer = c.createBuffer(1, c.sampleRate * seconds, c.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i += 1) {
        const white = Math.random() * 2 - 1;
        last = last * 0.985 + white * 0.015;
        data[i] = last;
    }
    const source = c.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    return source;
}

export const audioEngine = {
    init() {
        getContext();
    },
    setMuted(muted) {
        const c = getContext();
        if (!c || !master) return;
        master.gain.cancelScheduledValues(c.currentTime);
        master.gain.linearRampToValueAtTime(muted ? 0.0001 : 0.48, c.currentTime + 0.08);
    },
    playClick() {
        tone(690, 0.042, "square", 0.078);
        tone(1060, 0.026, "sine", 0.032, null, 0.014);
    },
    playBackspace() {
        tone(285, 0.064, "square", 0.075);
    },
    playError() {
        tone(170, 0.34, "sawtooth", 0.17, 76);
        noise(0.18, 0.03, 420);
    },
    playGranted() {
        tone(420, 0.1, "sine", 0.14);
        tone(620, 0.12, "sine", 0.16, null, 0.1);
        tone(930, 0.28, "sine", 0.18, null, 0.22);
    },
    playPowerDrop() {
        tone(86, 0.42, "sine", 0.12, 31);
        tone(48, 0.48, "triangle", 0.08, 24, 0.03);
        noise(0.16, 0.022, 280, 0.02, "lowpass");
    },
    playStartup() {
        tone(34, 3.15, "sine", 0.26, 118);
        tone(57, 2.6, "triangle", 0.095, 190, 0.18);
        tone(112, 1.85, "sine", 0.045, 310, 0.62);
        noise(2.2, 0.045, 310, 0.12, "lowpass");
        relayCluster(3, 0.17, 0.55);
    },
    playRelay(delay = 0) {
        relayCluster(1, 0.1, delay);
    },
    playPowerWave() {
        tone(135, 0.9, "triangle", 0.16, 880);
        tone(63, 0.72, "sine", 0.12, 105);
        noise(0.58, 0.055, 1250, 0.03);
    },
    playLightingSequence() {
        relayCluster(7, 0.105, 0);
        tone(240, 0.72, "sine", 0.045, 390, 0.25);
    },
    playMechanics() {
        tone(52, 1.35, "triangle", 0.13, 74);
        noise(0.85, 0.045, 520, 0.08, "lowpass");
        relayCluster(4, 0.14, 0.22);
        tone(116, 0.42, "square", 0.032, 92, 0.68);
    },
    playCoreIgnition() {
        tone(44, 1.8, "sine", 0.24, 96);
        tone(92, 1.35, "triangle", 0.115, 340, 0.25);
        noise(0.9, 0.038, 1450, 0.18);
        heartbeat(0.24, 0.8);
    },
    playProduction() {
        relayCluster(3, 0.11, 0);
        tone(310, 0.34, "sine", 0.07, 520, 0.32);
        tone(780, 0.2, "sine", 0.045, 1040, 0.58);
    },
    playHeartbeat() {
        heartbeat(0.12, 0);
    },
    playFinalSync() {
        heartbeat(0.26, 0);
        heartbeat(0.19, 0.31);
        tone(94, 1.28, "sine", 0.17, 460, 0.05);
        tone(460, 0.88, "triangle", 0.08, 920, 0.22);
        noise(0.62, 0.048, 1650, 0.17);
    },
    startHum() {
        if (humOsc) return;
        const c = getContext();
        if (!c || !master) return;

        humGain = c.createGain();
        humGain.gain.value = 0.0001;
        humGain.connect(master);

        humOsc = c.createOscillator();
        humOsc.type = "sine";
        humOsc.frequency.value = 46;
        humOsc.connect(humGain);
        humOsc.start();

        humOsc2 = c.createOscillator();
        const hum2Gain = c.createGain();
        humOsc2.type = "triangle";
        humOsc2.frequency.value = 92;
        hum2Gain.gain.value = 0.18;
        humOsc2.connect(hum2Gain);
        hum2Gain.connect(humGain);
        humOsc2.start();

        humNoise = makeLoopNoise(c);
        const filter = c.createBiquadFilter();
        humNoiseGain = c.createGain();
        filter.type = "lowpass";
        filter.frequency.value = 420;
        humNoiseGain.gain.value = 0.12;
        humNoise.connect(filter);
        filter.connect(humNoiseGain);
        humNoiseGain.connect(humGain);
        humNoise.start();

        humGain.gain.linearRampToValueAtTime(0.055, c.currentTime + 2.2);
    },
    stopHum() {
        if (!humOsc || !humGain || !ctx) return;
        humGain.gain.cancelScheduledValues(ctx.currentTime);
        humGain.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
        const osc1 = humOsc;
        const osc2 = humOsc2;
        const noiseSource = humNoise;
        humOsc = null;
        humOsc2 = null;
        humGain = null;
        humNoise = null;
        humNoiseGain = null;
        setTimeout(() => {
            try { osc1.stop(); } catch {}
            try { osc2?.stop(); } catch {}
            try { noiseSource?.stop(); } catch {}
        }, 340);
    }
};
